/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import { HttpEvent, HttpEventType }                                            from '@angular/common/http';

import {
  catchError,
  first,
  tap
}                                            from 'rxjs/operators';
import {
  forkJoin,
  Observable,
  of,
  Subscriber
}                                            from 'rxjs';

import { ProjectElement }                    from './project.element';
import { ProjectElementType }                from './project.element.type';
import { InputsElement }                     from './inputs.element';
import {
  Blob as FileBlob,
  BlobType
}                                            from '../blob';
import {
  isset,
  empty,
  dirname, basename, errorAsText
}                                            from '@/util/util';
import { Logger }                            from '@/util/logger';
import { SimConfig }                         from '@/models/sim/sim.config';
import { SimpleSimJob }                      from '@/models/sim/simple.sim.job';
import {
  SimulationJobType,
  SimulationStatusType
}                                            from '@/models/sim/simulation.job';
import { Project }                           from '../project';
import { JSONBrowserMetadata }               from '@/screens/ide/json.browser.metadata';
import { TextEditorMetadata }                from '@/screens/ide/text.editor.metadata';
import { ScenarioBrowserMetadata }           from '@/screens/ide/scenario.browser.metadata';
import { WorkBloxEditorMetadata }            from '@/screens/ide/work.blox.editor.metadata';
import { DiversionEditorMetadata }           from '@/screens/ide/diversion.editor.metadata';
import { TSOEditorMetadata }                 from '@/screens/ide/t.s.o.editor.metadata';
import { LineSimJobElement }                 from '@/models/project/line.sim.job.element';
import { ProjectElementFactory }             from '@/models/project.element.factory';
import { TaskUpdateFileUploadFailed }        from '@/models/task.update.file.upload.failed';
import { TaskUpdate }                        from '@/models/task.update';
import { TaskUpdateFileUpload }              from '@/models/task.update.file.upload';
import { TaskUpdateFileUploadComplete }      from '@/models/task.update.file.upload.complete';
import { ProjectElementFactoryDecorator }    from '@/models/project/project.element.factory.decorator';
import { ContentAwareTyping }                from '@/helpers/content.aware.typing';
import { UserCommandsElement }               from '@/models/project/user.commands.element';
import { UserCommandMergeResult }            from '@/models/user.command.merge.result';
import { UserCommandsResultElement }         from '@/models/project/user.commands.result.element';
import { TaskMergeUserCommands }             from '@/models/task.merge.user.commands';
import { TaskMergeUserCommandsFailed }       from '@/models/task.merge.user.commands.failed';
import { TaskMergeUserCommandsComplete }     from '@/models/task.merge.user.commands.complete';
import { DiamondCrossingsElement }           from '@/models/project/diamond.crossings.element';
import { DiamondCrossingsMergeResult }       from '@/models/diamond.crossings.merge.result';
import { DiamondCrossingsResultElement }     from '@/models/project/diamond.crossings.result.element';
import { TaskMergeDiamondCrossings }         from '@/models/task.merge.diamond.crossings';
import { TaskMergeDiamondCrossingsFailed }   from '@/models/task.merge.diamond.crossings.failed';
import { TaskMergeDiamondCrossingsComplete } from '@/models/task.merge.diamond.crossings.complete';
import { ActionService }                     from '@/services/action.service';
import { ServiceLocator }                    from '@/services/service.locator';
import { ActionType }                        from '@/models/action';
import { FileBrowserMetadata }               from '@/screens/ide/file.browser.metadata';
import { ConnectionViewerMetadata }          from '@/screens/ide/connection.viewer.metadata';

/**
 *
 * @class BlobElement
 *
 * @brief this project leaf is for any kind of blob that is allowed in the containing element.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
@ProjectElementFactoryDecorator('BlobElement')
export class BlobElement extends InputsElement {

  /** standard constructor */
  constructor(
     project: Project,
          id: string,
        type: BlobType,
         key: string,
      parent: ProjectElement,
    publish?: boolean,
      label?: string,
 elementType: ProjectElementType=ProjectElementType.Blob
  ) {
    super(project, id, null, publish, elementType);

    this._blobKey         = key;
    this._blobType        = type;
    this.actionKeys       = [
      'simulate',
      'newemptyinput',
      'adduploadinput',
      'addblobinput',
      'workbloxedit',
      'diversionsedit',
      'tsoedit',
      'scenariobrowseblob',
      'jsoneditblob',
      'raweditblob',
      'download',
      'downloadas',
      'cloneblob',
      'renameblob',
      'deleteblob',
      'generatekpitable',
      'viewinbrowser',
      'connectionviewer'
    ];

    this._acceptsElements = [ProjectElementType.None];

    this._acceptsBlobs.push(BlobType.None);

    this._enabled     = true;
    this._elementKind = 'Blob';

    if (label) {

      this._label = label;

    } else {

      /* the name should come from the blob */

      /* @link https://stackoverflow.com/questions/3820381/need-a-basename-function-in-javascript */

      let base = this._blobKey.substring(this._blobKey.lastIndexOf('/') + 1);

      if (base.lastIndexOf('.') !== -1) {

        base = base.substring(0, base.lastIndexOf('.'));
      }

      this._label = base;
    }

    this.updateIcon();

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType(): void {

    console.log("requiring BlobElement");
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any): void {

    super.fromNode(node);

    this._icon   = 'blob-icons/' + FileBlob.iconOfType(this._blobType);
  }

  public superDoDrop(data, parent: InputsElement) {

    super.doDrop(data, parent);
  }

  /**
   * helper to take an uploaded train movement, and save to a fake result, then use
   * the key of that, to make a fake result element child of this element.
   */
  public uploadResultToKey(outputData: string, fileName: string) {

    Logger.debug(`[BlobElement] creating fake output result (sz=${outputData.length}).`);

    let bel = this;

    this.busy();

    bel._project.services.uuid.generate()
      .pipe(first())
      .subscribe(
        (uuidData: any) => {

          /* figure out the kpi table blob location */

          const          uuid: string = uuidData.guid;
          const projectFolder: string = bel._project.dirname();
          const resultsFolder: string = projectFolder + `/fake-results/${uuid}/`;
          const       fakeKey: string = resultsFolder + bel.basename(fileName) + '.res';
          const      uploadId: string = TaskUpdate.generateId();

          Logger.debug(`[BlobElement] creating fake result ${fakeKey} ...`);

          let request = bel._project.services.blobber.create(
            fakeKey,
            BlobType.SimResult,
            outputData, 'fake sim results',
            'fake sim results',
            false,
            true

          ).pipe(
            catchError((err) => {

              Logger.debug(`[BlobElement]  ERROR ${fakeKey}`, err);

              let message = "Upload Failed!";

              if (isset(err.statusText)) {

                message = err.statusText;
              }

              if (isset(err.error.message)) {

                message = err.error.message;
              }

              let update = new TaskUpdateFileUploadFailed(uploadId, fakeKey, 'file-json-32.png', message);

              Logger.debug(`[BlobElement] ] progress: ${fakeKey} uploaded FAILED`, update);

              bel._project.services.taskMonitor.progress(update);

              return of(null);

          }));

          forkJoin([request].map(
            o => o.pipe(

              tap((e: HttpEvent<any>) => {

                if (!e) {

                  return;
                }

                if (e.type == HttpEventType.UploadProgress) {

                  /* upload progress */

                  let update = new TaskUpdateFileUpload(uploadId, fakeKey, 'file-json-32.png', e.loaded, outputData.length);

                  Logger.debug(`[FileBrowserWindow] progress: ${fakeKey} `, update);

                  bel._project.services.taskMonitor.progress(update);

                } else if (e.type == HttpEventType.Sent) {

                  /* the request was sent */

                } else if (e.type == HttpEventType.ResponseHeader) {

                  /* we got a response back, this means the upload was completed. */

                  let update = new TaskUpdateFileUploadComplete(uploadId, fakeKey, 'file-json-32.png', outputData.length);

                  Logger.debug(`[FileBrowserWindow] progress: ${fakeKey} uploaded COMPLETE`, update);

                  bel._project.services.taskMonitor.progress(update);
                }
              })

            ))).subscribe(

              () => {

                bel.unBusy();

                bel.createFakeResult(fakeKey, uuid);

              },
              (error) => {

                bel.unBusy();

                if (!error) {

                  return ;
                }

                Logger.error(`[BlobElement] could not create fake result in project.`, error);
              });

        }, (error) => {

          bel.unBusy();

          Logger.error(`[BlobElement] could not generate uuid for creating fake result key.`, error);
        });

  }

  /**
   * helper to take an existing blob key, that we know is a train movement, and clone it to
   * a fake result, then use the key of that, to make a fake result element child of this element.
   */
  public cloneResultKeyToKey(cloneKey: string) {

    Logger.debug(`[BlobElement] clone to fake output file: `, cloneKey);

    this._project.services.uuid.generate()
      .pipe(first())
      .subscribe(
        (uuidData: any) => {

          /* figure out the kpi table blob location */

          const          uuid: string = uuidData.guid;
          const projectFolder: string = this._project.dirname();
          const resultsFolder: string = projectFolder + `/fake-results/${uuid}/`;
          const     resultKey: string = resultsFolder + this.basename(cloneKey) + '.res';

          Logger.debug(`[BlobElement] cloning ${cloneKey} to fake result ${resultKey}...`);

          this._project.services.blobber.copy(cloneKey, resultKey)
            .pipe(first())
            .subscribe(
              () => {

                this.unBusy();

                this.createFakeResult(resultKey, uuid);

              }, (error) => {

                this.unBusy();

                Logger.error(`[BlobElement] could not copy fake result to project.`, error);
              });

        },
        (error) => {

          this.unBusy();

          Logger.error(`[BlobElement] could not generate uuid for cloning result key.`, error);
        });

  }

  /** helper to create FakeSimResultElement using the given train movement/output data key */
  public createFakeResult(fakeKey: string, uuid: string) {

    Logger.debug(`[BlobElement] creating fake result element via `, fakeKey);

    /*
     * create a job and element as usual, but the only thing valid about it will be the scenario
     * key (i.e. this element), and the output key (the fake key we just uploaded/cloned).  There
     * won't be any log or actual results, and its status will be forced to complete.
     *
     */

    const config: SimConfig = new SimConfig();

    const job: SimpleSimJob = new SimpleSimJob({
      type: SimulationJobType.SIMPLE_SIM,
      jobId: 0,
      jobUuid: uuid,
      status:  SimulationStatusType.SUCCESS,
      key: this._blobKey,
      instance: 'latest',
      config,
      created: new Date(),
      updated: new Date()
    });

    job.progress       = 100.0;
    job.localOutputKey = fakeKey;
    job.localLogkey    = '';

    // noinspection JSUnusedLocalSymbols
    const el: LineSimJobElement = ProjectElementFactory.fromObject(
      ProjectElementType.LineSimResult,
      {
        project: this._project,
        id:      '',
        job,
        parent:  this,
        publish: true
      }) as LineSimJobElement;

    /* make sure the thing we just created is visible */

    this._project.expand([this]);

    /* make sure the project is saved */

    this.save();
  }

  /**
   * clone the current (scenario) node to a new one, which has an additional
   * child for diamond crossings, and that new one also has the diamond crossings
   * "merged" into it.  We may also hang additional child nodes on the diamond
   * crossings child for showing validation results/errors.
   *
   * @param crossingsFile   File the diamond crossings file (no actually uploaded yet)
   * @param scenarioElement BlobElement the parent element
   *
   */
  public dropDiamondCrossings(crossingsFile: File, scenarioElement: BlobElement): void {

    const be: BlobElement = this;

    /*
     * figure out the source scenario, the commands file location (to upload to), and
     * the location of where to save the cloned scenario.
     *
     */

    const peers: ProjectElement[] = scenarioElement._parent._children;
    let label: string             = scenarioElement._label;

    /* try to find a none-conflicting name */

    let        n: number          = 1;
    let newLabel: string          = ``;

    while (n < 50) {

      newLabel =  label + `-DC-${n}`;

      if (peers.map(x => x._label).filter(x => x == newLabel).length === 0) {

        /* no clash of name */

        break ;
      }

      n++;
    }

    if (n >= 50) {

      /* can not make unique name */

      Logger.error('[BlobElement] dropDiamondCrossings can not make unique name for new scenario element.');

    }

    label = newLabel;

    /*
     * to keep things reasonably tidy, we create a sub-folder and put all the new stuff
     * in there.  The sub-folder name is the unique label.
     *
     */

    let scenario: string          = scenarioElement._blobKey;
    let folder: string            = dirname(scenario);
    const file: string            = basename(scenario);
    const ext: string             = file.substr(file.lastIndexOf('.') + 1).trim();
    const base: string            = file.substr(0, file.lastIndexOf('.'));
    let merged: string            = folder + '/' + label + '/' + base + '.' + ext
    let crossings: string         = folder + '/' + label + '/' + crossingsFile.name;

    /* the commands are ultimately merged into the scenario that we cloned */

    scenario                      = merged;

    Logger.debug(`[BlobElement] dropDiamondCrossings scenario=${scenario}, crossings=${crossings}, merged=${merged}`);

    /* allocate an ID to use for long-running progress update... */

    const updateId: string = TaskUpdate.generateId();

    /* initialize progress updating ... */

    let update: TaskMergeDiamondCrossings = new TaskMergeDiamondCrossings(updateId, scenario, crossings, merged);

    Logger.debug(`[BlobElement] dropDiamondCrossings starts...`, update);

    be._project.services.taskMonitor.progress(update);

    /*
     * clone scenarioElement to a new one at the same level (a peer) and ensure it has a
     * unique name.
     *
     */

    update = new TaskMergeDiamondCrossings(updateId, scenario, crossings, merged, 1, `Cloning ${basename(scenarioElement._blobKey)} => ${basename(scenario)}`);

    Logger.debug(`[BlobElement] dropDiamondCrossings cloning ${scenarioElement._blobKey} => ${scenario}.`, update);

    be._project.services.taskMonitor.progress(update);

    be._project.services.blobber.copy(scenarioElement._blobKey, scenario, 'latest', basename(scenario))
      .pipe(first())
      .subscribe(
        () => {

          Logger.debug(`[BlobElement] dropDiamondCrossings - blob cloned, adding element to tree...`);

          /* add a new child (peer) to the parent */

          let cloned: BlobElement = ProjectElementFactory.fromObject(
            ProjectElementType.Blob,
            {
              project: be._project,
              id:      '',
              type:    scenarioElement._blobType,
              key:     scenario,
              parent:  be._parent,
              publish: true,
              label
            }) as BlobElement;

          /*
           * add a child diamond crossings element to the cloned scenario element
           *
           */

          update = new TaskMergeDiamondCrossings(updateId, scenario, crossings, merged, 2, `Uploading ${basename(crossings)}`);

          Logger.debug(`[BlobElement] dropDiamondCrossings - uploading ${crossingsFile.name} => ${crossings}.`, update);

          be._project.services.taskMonitor.progress(update);

          be._project.services.blobber.create(crossings, BlobType.DiamondCrossings, crossingsFile, crossingsFile.name, 'input element')
            .pipe(first())
            .subscribe(
              () => {

                /* link the diamond crossings to the cloned scenario*/

                Logger.debug('[BlobElement] diamond crossings saved: ', crossings);

                let dc: DiamondCrossingsElement = ProjectElementFactory.fromObject(
                  ProjectElementType.DiamondCrossings,
                  {
                    project: this._project,
                    id:      '',
                    type:    BlobType.DiamondCrossings,
                    key:     crossings,
                    parent:  cloned,
                    publish: true
                  }) as DiamondCrossingsElement;

                /*
                 * add a child merge result element to the diamond crossing elements
                 *
                 */

                update = new TaskMergeDiamondCrossings(updateId, scenario, crossings, merged, 3, `Merging ${basename(crossings)} and ${basename(scenario)}`);

                Logger.debug(`[BlobElement] dropDiamondCrossings - merging ${crossings} with to ${scenario}.`, update);

                be._project.services.taskMonitor.progress(update);

                be._project.services.locoquery.mergeDiamondCrossings(scenarioElement._blobKey, crossings, scenario).then(
                  (result: DiamondCrossingsMergeResult|null): void => {

                    if (!result) {

                      /* could not merge? internal error? */

                      let fail: TaskMergeDiamondCrossingsFailed = new TaskMergeDiamondCrossingsFailed(updateId, scenario, 'Could not merge');

                      Logger.error(`[BlobElement] dropDiamondCrossings - could not merge`, fail);

                      be._project.services.taskMonitor.progress(fail);

                      /* abort! */

                      return ;
                    }

                    /*
                     * save the JSON version of the result to a blob, it might be too big to just embed
                     * directly in a project leaf.
                     *
                     */

                    let resultBlob: string = folder + '/' + label + '/' + base + '-result.json';

                    update = new TaskMergeDiamondCrossings(updateId, scenario, crossings, merged, 4, `Saving result ${basename(resultBlob)}`);

                    Logger.debug(`[BlobElement] dropDiamondCrossings - saving result ${resultBlob}.`, update);

                    be._project.services.taskMonitor.progress(update);

                    be._project.services.blobber.create(resultBlob, BlobType.Json, JSON.stringify(result.to_json()), basename(resultBlob), 'Diamond Crossings merge result element')
                      .pipe(first())
                      .subscribe(
                        () => {

                          /*
                           * the result was saved, we have a valid blob key and the result object, we can finally create
                           * the result element in the project tree.
                           *
                           */

                          Logger.debug('[BlobElement] diamond crossings merge result saved: ', resultBlob);

                          let mergeResult: DiamondCrossingsResultElement = ProjectElementFactory.fromObject(
                            ProjectElementType.DiamondCrossingsResult,
                            {
                              project: this._project,
                              id:      '',
                              type:    BlobType.Json,
                              key:     resultBlob,
                              parent:  dc,
                              publish: true,
                              data:    result
                            }) as DiamondCrossingsResultElement;

                          /* make sure the project is saved */

                          mergeResult.save();

                          /* we are done!  child elements have been added, and all persistent data has been stored. */

                          let done: TaskMergeDiamondCrossingsComplete = new TaskMergeDiamondCrossingsComplete(updateId, resultBlob);

                          Logger.debug(`[BlobElement] dropDiamondCrossings - COMPLETE`, done);

                          be._project.services.taskMonitor.progress(done);

                        },
                        (error) => {

                          let fail: TaskMergeDiamondCrossingsFailed = new TaskMergeDiamondCrossingsFailed(updateId, merged, error);

                          Logger.error(`[BlobElement] dropDiamondCrossings - saving result FAILED`, fail);

                          be._project.services.taskMonitor.progress(fail);

                        });

                  },
                  (error) => {

                    let fail: TaskMergeDiamondCrossingsFailed = new TaskMergeDiamondCrossingsFailed(updateId, scenario, error);

                    Logger.error(`[BlobElement] dropDiamondCrossings - could not merge`, fail);

                    be._project.services.taskMonitor.progress(fail);

                  });

              },
              (error) => {

                let fail: TaskMergeDiamondCrossingsFailed = new TaskMergeDiamondCrossingsFailed(updateId, merged, error);

                Logger.error(`[BlobElement] dropDiamondCrossings - cloning of scenario FAILED`, fail);

                be._project.services.taskMonitor.progress(fail);

              });

        }, (error) => {

          let fail: TaskMergeDiamondCrossingsFailed = new TaskMergeDiamondCrossingsFailed(updateId, merged, error);

          Logger.error(`[BlobElement] dropDiamondCrossings - merge FAILED`, fail);

          be._project.services.taskMonitor.progress(fail);

        });

  }

  /**
   * clone the current (scenario) node to a new one, which has an additional
   * child for user commands, and that new one also has the user commands
   * "merged" into it.  We may also hang additional child nodes on the user
   * commands child for showing validation results/errors.
   *
   * @param commandsFile    File the user commands file (no actually uploaded yet)
   * @param scenarioElement BlobElement the parent element
   *
   */
  public dropUserCommands(commandsFile: File, scenarioElement: BlobElement): void {

    const be: BlobElement = this;

    /*
     * figure out the source scenario, the commands file location (to upload to), and
     * the location of where to save the cloned scenario.
     *
     */

    const peers: ProjectElement[] = scenarioElement._parent._children;
    let label: string             = scenarioElement._label;

    /* try to find a none-conflicting name */

    let        n: number          = 1;
    let newLabel: string          = ``;

    while (n < 50) {

      newLabel =  label + `-UC-${n}`;

      if (peers.map(x => x._label).filter(x => x == newLabel).length === 0) {

        /* no clash of name */

        break ;
      }

      n++;
    }

    if (n >= 50) {

      /* can not make unique name */

      Logger.error('[BlobElement] dropUserCommands can not make unique name for new scenario element.');

    }

    label = newLabel;

    /*
     * to keep things reasonably tidy, we create a sub-folder and put all the new stuff
     * in there.  The sub-folder name is the unique label.
     *
     */

    let scenario: string          = scenarioElement._blobKey;
    let folder: string            = dirname(scenario);
    const file: string            = basename(scenario);
    const ext: string             = file.substr(file.lastIndexOf('.') + 1).trim();
    const base: string            = file.substr(0, file.lastIndexOf('.'));
    let merged: string            = folder + '/' + label + '/' + label + '.' + ext
    let commands: string          = folder + '/' + label + '/' + commandsFile.name;

    /* the commands are ultimately merged into the scenario that we cloned */

    scenario                      = merged;

    Logger.debug(`[BlobElement] dropUserCommands scenario=${scenario}, commands=${commands}, merged=${merged}`);

    /* allocate an ID to use for long-running progress update... */

    const updateId: string = TaskUpdate.generateId();

    /* initialize progress updating ... */

    let update: TaskMergeUserCommands = new TaskMergeUserCommands(updateId, scenario, commands, merged);

    Logger.debug(`[BlobElement] dropUserCommands starts...`, update);

    be._project.services.taskMonitor.progress(update);

    /*
     * clone scenarioElement to a new one at the same level (a peer) and ensure it has a
     * unique name.
     *
     */

    update = new TaskMergeUserCommands(updateId, scenario, commands, merged, 1, `Cloning ${basename(scenarioElement._blobKey)} => ${basename(scenario)}`);

    Logger.debug(`[BlobElement] dropUserCommands cloning ${scenarioElement._blobKey} => ${scenario}.`, update);

    be._project.services.taskMonitor.progress(update);

    be._project.services.blobber.copy(scenarioElement._blobKey, scenario, 'latest', basename(scenario))
      .pipe(first())
      .subscribe(
        () => {

          Logger.debug(`[BlobElement] dropUserCommands - blob cloned, adding element to tree...`);

          /* add a new child (peer) to the parent */

          let cloned: BlobElement = ProjectElementFactory.fromObject(
            ProjectElementType.Blob,
            {
              project: be._project,
              id:      '',
              type:    scenarioElement._blobType,
              key:     scenario,
              parent:  be._parent,
              publish: true,
              label
            }) as BlobElement;

          /*
           * add a child user commands element to the cloned scenario element
           *
           */

          update = new TaskMergeUserCommands(updateId, scenario, commands, merged, 2, `Uploading ${basename(commands)}`);

          Logger.debug(`[BlobElement] dropUserCommands - uploading ${commandsFile.name} => ${commands}.`, update);

          be._project.services.taskMonitor.progress(update);

          be._project.services.blobber.create(commands, BlobType.UserCommands, commandsFile, commandsFile.name, 'input element')
            .pipe(first())
            .subscribe(
              () => {

                /* link the user commands to the cloned scenario*/

                Logger.debug('[BlobElement] user commands saved: ', commands);

                let uc: UserCommandsElement = ProjectElementFactory.fromObject(
                  ProjectElementType.UserCommands,
                  {
                    project: this._project,
                    id:      '',
                    type:    BlobType.UserCommands,
                    key:     commands,
                    parent:  cloned,
                    publish: true
                  }) as UserCommandsElement;

                /*
                 * add a child merge result element to the user commands elements
                 *
                 */

                update = new TaskMergeUserCommands(updateId, scenario, commands, merged, 3, `Merging ${basename(commands)} and ${basename(scenario)}`);

                Logger.debug(`[BlobElement] dropUserCommands - merging ${commands} with to ${scenario}.`, update);

                be._project.services.taskMonitor.progress(update);

                be._project.services.locoquery.mergeUserCommand(scenarioElement._blobKey, commands, scenario).then(
                  (result: UserCommandMergeResult|null): void => {

                    if (!result) {

                      /* could not merge? internal error? */

                      let fail: TaskMergeUserCommandsFailed = new TaskMergeUserCommandsFailed(updateId, scenario, 'Could not merge');

                      Logger.error(`[BlobElement] dropUserCommands - could not merge`, fail);

                      be._project.services.taskMonitor.progress(fail);

                      /* abort! */

                      return ;
                    }

                    /*
                     * save the JSON version of the result to a blob, it might be too big to just embed
                     * directly in a project leaf.
                     *
                     */

                    let resultBlob: string = folder + '/' + label + '/' + base + '-result.json';

                    update = new TaskMergeUserCommands(updateId, scenario, commands, merged, 4, `Saving result ${basename(resultBlob)}`);

                    Logger.debug(`[BlobElement] dropUserCommands - saving result ${resultBlob}.`, update);

                    be._project.services.taskMonitor.progress(update);

                    be._project.services.blobber.create(resultBlob, BlobType.Json, JSON.stringify(result.to_json()), basename(resultBlob), 'User commands merge result element')
                      .pipe(first())
                      .subscribe(
                        () => {

                          /*
                           * the result was saved, we have a valid blob key and the result object, we can finally create
                           * the result element in the project tree.
                           *
                           */

                          Logger.debug('[BlobElement] user commands merge result saved: ', resultBlob);

                          let mergeResult: UserCommandsResultElement = ProjectElementFactory.fromObject(
                            ProjectElementType.UserCommandsResult,
                            {
                              project: this._project,
                              id:      '',
                              type:    BlobType.Json,
                              key:     resultBlob,
                              parent:  uc,
                              publish: true,
                              data:    result
                            }) as UserCommandsResultElement;

                          /* make sure the project is saved */

                          mergeResult.save();

                          /* we are done!  child elements have been added, and all persistent data has been stored. */

                          let done: TaskMergeUserCommandsComplete = new TaskMergeUserCommandsComplete(updateId, resultBlob);

                          Logger.debug(`[BlobElement] dropUserCommands - COMPLETE`, done);

                          be._project.services.taskMonitor.progress(done);

                        },
                        (error) => {

                          let fail: TaskMergeUserCommandsFailed = new TaskMergeUserCommandsFailed(updateId, merged, error);

                          Logger.error(`[BlobElement] dropUserCommands - saving result FAILED`, fail);

                          be._project.services.taskMonitor.progress(fail);

                        });

                  },
                  (error) => {

                    let fail: TaskMergeUserCommandsFailed = new TaskMergeUserCommandsFailed(updateId, scenario, error);

                    Logger.error(`[BlobElement] dropUserCommands - could not merge`, fail);

                    be._project.services.taskMonitor.progress(fail);

                  });

              },
              (error) => {

                let fail: TaskMergeUserCommandsFailed = new TaskMergeUserCommandsFailed(updateId, merged, error);

                Logger.error(`[BlobElement] dropUserCommands - cloning of scenario FAILED`, fail);

                be._project.services.taskMonitor.progress(fail);

              });

        }, (error) => {

          let fail: TaskMergeUserCommandsFailed = new TaskMergeUserCommandsFailed(updateId, merged, error);

          Logger.error(`[BlobElement] dropUserCommands - merge FAILED`, fail);

          be._project.services.taskMonitor.progress(fail);

        });

  }

  public doDrop(data, parent: InputsElement): boolean {

    if (((data.files.length === 0) || (data.files.length > 1)) &&
        !data.dataTransfer.types.find(x => x === 'application/json')) {

      /* can't possibly be a single output they are dragging on to a presumed scenario */

      Logger.debug('[BlobElement] can not be single output file drop, doing default drop...');

      return super.doDrop(data, parent);
    }

    /*
     * in the case where this element is a scenario and the thing being dropped
     * is an output...we can make a fake job; instead of running a simulation to
     * create a LineSimJobElement, we just use this blob and the given (output)
     * blob to directly make a FakeSimJobElement.
     *
     * If that special case doesn't apply...we fall through doing what we usually
     * do via InputElement.
     */

    Logger.debug('[BlobElement] dropping in blob element, checking if its a scenario...');

    const be: BlobElement = this;

    this.busy();

    /*
     * NOTE this is kind of a legacy way of knowing if the drop zone element is a
     * scenario; the newer version of InputsElement creates scenarios blobs with
     * the 'scenario' type (because it now does content aware typing), plus we could
     * also test by just looking at the ancestor tree:
     *
     *    Simulations >> <sim name> >> Inputs >> <self>
     *
     * If we see that path, and this is a JSON file...It's pretty much guaranteed to be
     * a scenario.
     *
     * For now, I'll leave this as is, since older projects will have scenarios that are
     * just "file" blobs instead of "scenario" blobs.  The ancestor path check should
     * work either way, so we could pick up some performance and simplify a bit.  Future
     * maintenance work.
     *
     */

    be._project.services.blobber.isScenario(be._blobKey, be._blobInstance)
      .pipe(first())
      .subscribe(
        (answer: any) => {

          this.unBusy();

          if (answer.is === true) {

            /*
             * we know the parent (this element) is a scenario, now we have to confirm the thing
             * being dropped is a simulation output...
             *
             */

            if (data.files.length > 0) {

              const files: File[]     = Array.from(data.files);
              const  file: File       = files[0];
              let sample: string      = '';
              const readLimit: number = 1024;

              /*
               * before we can do the usual behavior of trying to pair a simulation result with
               * a scenario...we have to check the typing of file being dropped; it's likely a
               * generic .txt or .json...so we need to do content aware typing...this lets us
               * look inside and determine if It's something special like a user commands file.
               *
               * If it is, then we can do whatever special behavior is needed, before we do the
               * default stuff.  The only catch, is that to peek inside the file, we have to do
               * asynchronous reading.  So we wrap FileReader with an observer and then do the
               * usual .subscribe() on that.
               *
               */

              Logger.debug(`[BlobElement] content aware typing...`)

              let obs: Observable<Object> = new Observable((sub: Subscriber<string>): void => {

                Logger.debug(`[BlobElement] . . new observer: ${file.name}: `);

                const fileReader: FileReader = new FileReader();

                /* when the read is completed */

                fileReader.onload = (ev: ProgressEvent<FileReader>): void => {

                  let bytes: Uint8Array = new Uint8Array((<any>ev.target).result);

                  for (let i = 0; i < bytes.length; i++) {

                    sample += String.fromCharCode(bytes[i]);
                  }

                  /* save our own mapping of the sample data */

                  Logger.debug(`[BlobElement] . sample [${ev.type}] ${file.name}: `, sample.substr(0, 64));

                  sub.next(sample);
                  sub.complete();
                }

                /* when the read fails */

                fileReader.onerror = (error: ProgressEvent<FileReader>): void => {

                  sub.error(error);
                };

                /* trigger the file reading (asynchronously) ... */

                fileReader.readAsArrayBuffer(file.slice(0, readLimit));

              });

              obs.subscribe(
                (text: string): void => {

                  /* are they dropping a user commands file on this scenario? */

                  let override: string|null = ContentAwareTyping.typeByContent(sample);

                  Logger.debug(`[BlobElement] content aware type (${file.name}): `, override);

                  if (override &&
                      (FileBlob.typeofString(override) != BlobType.UserCommands) &&
                      (FileBlob.typeofString(override) != BlobType.DiamondCrossings)) {

                    /*
                     * current you can only drop a .txt or a .csv on a scenario; .txt is for user
                     * commands, while .csv is for diamond crossings. If content aware type
                     * checking didn't pick up one of those, then we check the file extension; it
                     * has to be one of those, otherwise it's not supported.
                     *
                     */

                    if (/\.txt$/.test(file.name) || /\.uc$/.test(file.name)) {

                      override = FileBlob.stringOfType(BlobType.UserCommands);

                      Logger.debug(`[BlobElement] content aware type (${file.name}): presumed user commands file (its a .txt/.uc file).`);

                    } else if (/\.csv$/.test(file.name) || /\.dc$/.test(file.name)) {

                      override = FileBlob.stringOfType(BlobType.DiamondCrossings);

                      Logger.debug(`[BlobElement] content aware type (${file.name}): presumed diamond crossings file (its a .csv/.dc file).`);

                    }
                  }

                  if (override && (FileBlob.typeofString(override) == BlobType.UserCommands)) {

                    be.dropUserCommands(Array.from(data.files as File[])[0], be);

                    return ;

                  } else if (override && (FileBlob.typeofString(override) == BlobType.DiamondCrossings)) {

                    be.dropDiamondCrossings(Array.from(data.files as File[])[0], be);

                    return ;
                  }

                  Logger.debug(`[BlobElement] falling through to default scenario drop zone behavior...`);

                  /*
                   * if we fall through, it means it's not recognized as a special action, just
                   * do the default behavior, which is to assume they are dropping a simulation
                   * result, to create a fake scenario/output pair.
                   *
                   */

                  /* they are dragging a file from the local desk top, we presume an output result to pair up */

                  /* we have to ready the file to recover the content to test it... */

                  Logger.debug(`[BlobElement] pre-scanning (${file.name}) to confirm its an output ...`);

                  const reader = new FileReader();

                  reader.onerror = (msg) => {

                    Logger.error(`[BlobElement] could not ready file ${file.name}`, msg);

                    /* fall through to doing whatever InputElement would do */

                    Logger.debug('[BlobElement] (error) falling through to normal drop behavior...');

                    return be.superDoDrop(data, parent);
                  };

                  reader.onload = (event: ProgressEvent<FileReader>) => {

                    const text     = event.target.result;
                    const str               = String(text);
                    let obj: any                = {};

                    /* watch out for invalid JSON, maybe they are dropping some rando file */

                    try {

                      obj = JSON.parse(str);

                    } catch (e) {

                      Logger.debug(`[BlobElement] dropped blob is not valid JSON (can't be an output result), JSON parse error: `, errorAsText(e));

                      return be.superDoDrop(data, parent);
                    }

                    if (isset(obj.sim_results) &&
                        isset(obj.sim_results.datapoints) &&
                        isset(obj.sim_results.datapoints.data)) {

                      /*
                       * we've got a lock, the dropped file is sim result, we need to upload it, so we
                       * can map it to fake key...
                       *
                       */

                      be.uploadResultToKey(str, file.name);

                    } else {

                      Logger.debug('[BlobElement] dropped blob is not a sim output, falling through to normal drop behavior...');

                      return be.superDoDrop(data, parent);
                    }

                  };

                  reader.readAsText(file);

                },
                (error): void => {

                  Logger.debug('[BlobElement] problems during content aware check: ', errorAsText(error));

                  return be.superDoDrop(data, parent);
                }
              )

            } else {

              /* they are dragging a blob key from the blob Browser */

              /* get the key, then use the blobber to test if it's a sim output blob */

              const json: any      = JSON.parse(data.dataTransfer.getData('application/json'));
              const keys: string[] = [];

              json.forEach((item: any) => {

                keys.push(item.key);
              });

              const key: string = keys[0];

              Logger.debug(`[BlobElement] checking if blob ${key} is sim output ...`);

              be._project.services.blobber.isOutput(key, 'latest')
                .pipe(first())
                .subscribe(
                  (answer: any) => {

                    if(answer.is === true) {

                      be.cloneResultKeyToKey(key);

                    } else {

                      Logger.debug('[BlobElement] dropped blob key is not a sim output, falling through to normal drop behavior...');

                      return be.superDoDrop(data, parent);
                    }

                  },
                  (error) => {

                    Logger.error('[BlobElement] could not determine type of (output) blob key.', error);

                    /* fall through to doing whatever InputElement would do */

                    Logger.debug('[BlobElement] (error) falling through to normal drop behavior...');

                    return be.superDoDrop(data, parent);

                  });

            }

          } else {

            Logger.debug('[BlobElement] this blob is not a scenario, falling through to normal drop behavior...');

            return be.superDoDrop(data, parent);
          }

        },
        (error) => {

          this.unBusy();

          Logger.error('[BlobElement] could not determine type of (scenario) blob.', error);

          /* fall through to doing whatever InputElement would do */

          Logger.debug('[BlobElement] (error) falling through to normal drop behavior...');

          return be.superDoDrop(data, parent);
        });

    return true;
  }

  /** can this element be a drop site of drag and drop? */
  public acceptsDrop(data): boolean {

    return true;
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return this.doDrop(data, this._parent as InputsElement);
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    if (this._blobType == BlobType.Scenario) {

      actionSet.simulate = {
        name: 'Simulate',
        icon: 'fas fa-play',
        disabled: false
      };
    }

    actionSet.scenario = {

      name: 'Scenario',
      items: {
        scenariobrowseblob: {
          name: 'Browse Scenario',
          icon: 'fas fa-table',
          disabled: false
        },

        connectionviewer: {
          name: 'Connections...',
          icon: 'fas fa-project-diagram',
          disabled: false
        },

        workbloxedit: {
          name: 'Edit Work Blocks',
          icon: 'fas fa-snowplow',
          disabled: false
        },

        diversionsedit: {
          name: 'Edit Diversions',
          icon: 'fas fa-route',
          disabled: false
        },

        tsoedit: {
          name: 'Edit TSO',
          icon: 'fas fa-tools',
          disabled:  this.isBusy()
        },

        generatekpitable: {
          name: 'Generate KPI Table',
          icon: 'far fa-chart-bar',
          disabled:  this.isBusy()
        }
      }
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.tools = {

      name: 'Tools',
      items: {

        jsoneditblob:  {
          name: 'JSON View',
          icon: 'fas fa-edit',
          disabled: false
        },

        raweditblob: {
          name: 'Raw View',
          icon: 'fas fa-keyboard',
          disabled: false
        },

        download: {
          name: 'Download',
          icon: 'fas fa-file-download',
          disabled: false
        },

        downloadas: {
          name: 'Download As...',
          icon: 'fas fa-file-download',
          disabled: false
        },
      }
    };

    actionSet.cloneblob = {
      name: 'Clone',
      icon: 'fas fa-plus',
      disabled: false
    };

    actionSet.renameblob = {
      name: 'Rename',
      icon: 'fas fa-tag',
      disabled:  this.isBusy()
    };

    actionSet.deleteblob = {
      name: 'Delete',
      icon: 'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    /*
    actionSet.newemptyinput = {
      name: 'New (Empty)',
      icon: 'far fa-file',
      disabled: false
    };

    actionSet.addblobinput = {
      name: 'Add (Clone)',
      icon: 'fas fa-plus',
      disabled: false
    };

    actionSet.adduploadinput = {
      name: 'Add (Upload)',
      icon: 'fas fa-file-upload',
      disabled: this.pending.length !== 0
    };
    */

    return actionSet;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    if (this.isBusy()) {

      return false;
    }

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if (['newemptyinput', 'adduploadinput', 'addblobinput'].find(x => x === key)) {

      /* let InputElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'simulate': {

        Logger.debug('[BlobElement] invoking simulate action...');

        /* simulation action can be invoked via the simulation button and the simulation action */

        const actions: ActionService = ServiceLocator.injector.get(ActionService);

        actions.action(ActionType.Window_SimpleSim).execute();
      }
        break;

      case 'generatekpitable': {

        this._project.generateKPITable(this);
      }
      break;

      case 'tsoedit': {

        this._project.services.ide.launchWindow(new TSOEditorMetadata(
          '',
          '',
          this._blobKey
        ));

        break;
      }

      case 'diversionsedit': {

        this._project.services.ide.launchWindow(new DiversionEditorMetadata(
          '',
          '',
          this._blobKey
        ));

        break;
      }

      case 'connectionviewer': {

        this._project.services.ide.launchWindow(new ConnectionViewerMetadata(
          '',
          '',
          this._blobKey,
        ));

        break;
      }

      case 'workbloxedit': {

        this._project.services.ide.launchWindow(new WorkBloxEditorMetadata(
          '',
          '',
          this._blobKey
        ));

        break;
      }

      case 'scenariobrowseblob': {

        this._project.services.ide.launchWindow(new ScenarioBrowserMetadata(
          '',
          '',
          this._blobKey
        ));

        break;
      }

      case 'jsoneditblob': {

        this._project.services.ide.launchWindow(new  JSONBrowserMetadata(
          '',
          '',
          this._blobKey,
          'json',
          basename(this._blobKey),
          '<i class="fas fa-edit"></i>'
        ));

        break;
      }

      case 'raweditblob': {

        this._project.services.ide.launchWindow(new TextEditorMetadata(
          '',
          '',
          this._blobKey
        ));

        break;
      }

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._blobKey, this._blobKey));

        break;
      }

      case 'download': {

        this.downloadBlob();

        break;
      }

      case 'downloadas': {

        this.downloadBlobAs();

        break;
      }

      case 'cloneblob': {

        /**
         *
         * cloning  a blob means just cloning the primary blob, and that's it.  No secondary
         * stuff, no children...just the blob they asked to clone.  So our goal here is to get
         * a name for the clone, and then clone it; creating a new peer element and a new blob
         * in the process.
         *
         */

        const bb: BlobElement = this;

        /* use a dialog to get the new name and then re-key any children... */

        bb._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[BlobElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Blob name can not be empty.');

              return;
            }

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = bb._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Blob of that name already exists.');

                return;
              }

            }

            resolve(name);

          },
          (name) => {

            Logger.debug(`[BlobElement] cloning ${bb._label} to: `, name);

            /* figure out the destination path for the clone ... */

            let      ext: string = bb._blobKey.substr(bb._blobKey.lastIndexOf('.') + 1);
            const oldKey: string = bb._blobKey;

            if (ext === bb._blobKey) {

              ext = '';

            } else {

              ext = '.' + ext;
            }

            /* copy the blob ... */

            const newKey: string = dirname(oldKey) + '/' + name + ext;

            bb._project.services.blobber.copy(oldKey, newKey, 'latest', name)
              .pipe(first())
              .subscribe(
                () => {

                  Logger.debug(`[BlobElement] blob copied, adding new child...`);

                  /* add a new child (peer) to the parent */

                  ProjectElementFactory.fromObject(
                    ProjectElementType.Blob,
                    {
                      project: bb._project,
                      id:      '',
                      type:    bb._blobType,
                      key:     newKey,
                      parent:  bb._parent,
                      publish: true
                    });

                    bb.save();

                }, (error) => {

                  Logger.error('[BlobElement] problem cloning blob: ', error);
                });

          }, {
            title: 'Clone Blob',
            placeholder: this._label
          });
      }
      break;

      case 'renameblob': {

        this.renameBlob();

      }
      break;

      case 'deleteblob': {

        Logger.debug('[BlobElement] deleting', this._blobKey, this._blobType);

        this.deleteElements(selection);

      }
      break;
    }

  }

  public renameBlob(): void {

    const bb: BlobElement = this;

    /* use a dialog to get the new name and then re-key any children... */

    bb._project.services.modals.open(
      'rename-element-modal',
      (name, resolve, reject) => {

        Logger.debug('[BlobElement] validating: ', name);

        /* is it a valid name? */

        if (name.trim() === '') {

          reject('Blob name can not be empty.');

          return ;
        }

        /* make sure we aren't name clashing ...  */

        const peers: ProjectElement[] = bb._parent._children;

        for (const peer of peers) {

          if (peer._label === name) {

            reject('Blob of that name already exists.');

            return ;
          }

        }

        resolve(name);

      },
      (name) => {

        /*
         * do the actual renaming...we have to update the blob key with a new "basename";
         * and then search / replace that path on any children.
         *
         */

        /*
         * figure out what the path changes any child paths based on this element, will
         * not include the file extension (if there is one)
         *
         */

        let       ext: string = bb._blobKey.substr(bb._blobKey.lastIndexOf('.') + 1);
        const oldKey: string = bb._blobKey;

        if (ext === bb._blobKey) {

          ext = '';

        } else {

          ext = '.' + ext;
        }

        const newKey: string = dirname(oldKey) + '/' + name + ext;

        let ks: string = oldKey;
        let kr: string = newKey;

        if (!empty(ext)) {

          if (ks.lastIndexOf('.') > 0) {

            ks = ks.substring(0, ks.lastIndexOf('.'));
          }

          if (kr.lastIndexOf('.') > 0) {

            kr = kr.substring(0, kr.lastIndexOf('.'));
          }
        }

        /* if it doesn't have children...we're done already */

        if (bb._children.length === 0) {

          Logger.debug('[BlobElement] no children, renaming parent blob...');

          this.busy();

          bb._project.services.blobber.copy(oldKey, newKey, 'latest', name)
            .pipe(first())
            .subscribe({
              next: (): void => {

                this.unBusy();

                bb._label = name;

                bb._blobKey = newKey;

                bb.redraw();

                bb.save();

                /* can unlink the old one now ... */

                this._project.services.blobber.remove(oldKey)
                  .pipe(first())
                  .subscribe({

                    next: (): void => {

                      Logger.debug('[BlobElement] old blob deleted, rename complete.');

                    },

                    error: (err: any): void => {

                      Logger.error('[BlobElement] could not delete old blob: ', errorAsText(err));

                    }

                  });


              },

              error: (err: any): void => {

                this.unBusy();

                Logger.error('[BlobElement] problem re-naming blob: ', errorAsText(err));
              }

            });

          return ;
        }

        Logger.debug(`[BlobElement] renaming... ${ks} => ${kr}...`);

        /* adjust (in bulk)... */

        bb.shiftBlobs(ks, kr, (result) => {

          if (result !== true) {

            /* there was a problem! */

            Logger.error(`[BlobElement] could not properly rename children: `, result);

            return ;
          }

          Logger.debug('[BlobElement] children re-named, renaming parent blob...');

          this.busy();

          bb._project.services.blobber.copy(oldKey, newKey, 'latest', name)
            .pipe(first())
            .subscribe({
              next: (): void => {

                this.unBusy();

                bb._label = name;

                bb._blobKey = newKey;

                bb.redraw();

                bb.save();

                /* can unlink the old one now ... */

                this._project.services.blobber.remove(oldKey)
                  .pipe(first())
                  .subscribe(
                    () => {

                      Logger.debug('[BlobElement] old blob deleted, rename complete.');

                    }, (error) => {

                      Logger.error('[BlobElement] could not delete old blob: ', error);

                    });


              },
              error: (error) => {

                this.unBusy();

                Logger.error('[BlobElement] problem re-naming blob: ', error);
              }
            });


        }, [bb]);


      }, {
        title: 'Rename Blob',
        placeholder: this._label
      });

  }

  public downloadBlobAs(): void {

    Logger.debug('[BlobElement] saving as ...', this._blobKey, this._blobType);

    /*
     * use a dialog to get the file name to download it as, and once we have that
     * just do the download as normal, but override the file name.
     *
     */

    const bb: BlobElement = this;

    /* use a dialog to get the new name and then re-key any children... */

    bb._project.services.modals.open(
      'saveblobas-element-modal',
      (name, resolve, reject) => {

        Logger.debug('[BlobElement] saving as validating: ', name);

        /* we just always validate; we can't predicate what Window or Apple will think is valid */

        if (name.trim() === '') {

          reject('File name can not be empty.');

          return;
        }

        resolve(name);
      },
      (name) => {

        /* download as we normlaly would, just override the target file name */

        bb.downloadBlob(name);
      },
      {
        title: 'Download As...',
        placeholder: this._label
      });

  }

  public downloadBlob(saveAs: string|null = null): void {

    Logger.debug('[BlobElement] downloading: ', this._blobKey, this._blobType);

    const type: BlobType = FileBlob.guessType(this._blobType, this._blobKey, this._label);
    let   mime: string   = 'application/octet-stream';
    let    txt: boolean  = false;
    let keyBase          = this._blobKey.substring(this._blobKey.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = this._blobKey;

    }

    /* are the doing a "Save As..." and overriding the filename? */

    if (saveAs) {

      keyBase = saveAs;

      Logger.debug('[BlobElement] downloading - using save as target file name: ', keyBase);
    }

    /*
     * everything gets downloaded as binary data, but if it reasonably looks like JSON
     * data, download it as such.
     *
     */

    if (type !== BlobType.None && type !== BlobType.File) {

      /* our own file types are all JSON based */

      mime = 'application/json';
      txt  = true;

    } else {

      if (/\.json$/i.test(this._blobKey) || /\.json$/i.test(this._label)) {

        mime = 'application/json';
        txt  = true;
      }

      if (/\.csv/i.test(this._blobKey) || /\.csv$/i.test(this._label)) {

        mime = 'text/csv';
        txt  = true;
      }
    }

    /* fetch the blob */

    this._project.services.blobber.contents(this._blobKey, 'latest', txt ? 'text' : 'blob')
      .pipe(first())
      .subscribe(
        (content: any) => {

          Logger.debug(`[BlobElement] downloading - ${keyBase} => ${mime}`);

          let blob: Blob;

          if (typeof content === 'string') {

            /* it was presumed to be text */

            blob = new Blob([content], {type: mime});

          } else {

            /* its taken as a binary blob */

            blob = content as Blob;
          }

          const url  = window.URL.createObjectURL(blob);

          /* set up a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href = url;
          element.download = keyBase || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[BlobElement] downloading - can't find content ${this._blobKey}: `, error);
        });

  }

}
